import { HttpErrorResponse } from '@angular/common/http';

import { createSelector } from '@ngrx/store';

import {
  createEntityCollectionStore,
  memoizeSelector,
  ngrxEvent,
  pipeState,
} from '@recruitee/ngrx';
import type { OfferData, OfferFull, OfferStatus } from '@recruitee/offers-types';

import { EntityStoreKey, OfferScope } from '../../types';
import { StatusChangeType } from './../../types';

const {
  actions: defaultActions,
  namespace,
  selectors,
  reducer,
} = createEntityCollectionStore<OfferFull>()(EntityStoreKey.TalentPoolsFull, {
  // eslint-disable-next-line max-lines-per-function
  customActions: adapter => ({
    fetchOne: (state, payload: { id: number | string; langCode?: string }) =>
      adapter.markAsPending(state, { scope: null, entities: [payload.id] }),
    fetchOneSuccess: (state, payload: { id: number | string; offer: OfferFull }) =>
      pipeState(
        state,
        next => adapter.upsertOne(next, payload.offer),
        next => adapter.markAsLoaded(next, { scope: null, entities: [payload.id] }),
      ),
    fetchOneFailed: (state, payload: { id: number | string; error: HttpErrorResponse }) =>
      adapter.setError(state, payload.error, { scope: null, entities: [payload.id] }),
    follow: (state, payload: { id: number; scope: OfferScope }) =>
      adapter.markAsPending(state, {
        scope: null,
        entities: [payload.id],
      }),
    followSuccess: (state, payload: { talentPool: OfferFull }) =>
      pipeState(adapter.upsertOne(state, payload.talentPool), next =>
        adapter.markAsLoaded(next, {
          scope: null,
          entities: [payload.talentPool.id],
        }),
      ),
    followFailed: (state, payload: { error: HttpErrorResponse; id: number }) =>
      adapter.setError(state, payload.error, {
        scope: null,
        entities: [payload.id],
      }),
    unfollow: (state, payload: { id: number; scope: OfferScope }) =>
      adapter.markAsPending(state, {
        scope: null,
        entities: [payload.id],
      }),
    unfollowSuccess: (state, payload: { talentPool: OfferFull }) =>
      pipeState(adapter.upsertOne(state, payload.talentPool), next =>
        adapter.markAsLoaded(next, {
          scope: null,
          entities: [payload.talentPool.id],
        }),
      ),
    unfollowFailed: (state, payload: { error: HttpErrorResponse; id: number }) =>
      adapter.setError(state, payload.error, {
        scope: null,
        entities: [payload.id],
      }),
    create: ngrxEvent<{ offer: OfferData }>(),
    createSuccess: (state, payload: { data: OfferFull }) =>
      pipeState(adapter.upsertOne(state, payload.data)),
    createFailed: ngrxEvent<{ error: HttpErrorResponse & { errorFields: any } }>(),
    update: (state, payload: { offer: OfferData & { id: number } }) =>
      pipeState(adapter.markAsPending(state, { scope: null, entities: [payload.offer.id] })),
    updateSuccess: (state, payload: { id: number; data: OfferFull }) =>
      pipeState(adapter.updateOne(state, payload.data), next =>
        adapter.markAsLoaded(next, { scope: null, entities: [payload.id] }),
      ),
    updateFailed: (
      state,
      payload: { id: number; error: HttpErrorResponse & { errorFields: any } },
    ) => pipeState(adapter.setError(state, payload.error, { scope: null, entities: [payload.id] })),
    updateOne: (state, payload: OfferFull) => pipeState(adapter.updateOne(state, payload)),
    duplicate: ngrxEvent<{ id: number }>(),
    duplicateSuccess: (state, payload: { offer: OfferFull }) =>
      adapter.upsertOne(state, payload.offer),
    duplicateFailed: ngrxEvent<{ id: number; error: HttpErrorResponse & { errorFields: any } }>(),
    changeStatus: (
      state,
      payload: {
        id: number;
        status: StatusChangeType;
        currentStatus: OfferStatus;
      },
    ) =>
      adapter.markAsPending(state, {
        scope: null,
        entities: [payload.id],
      }),
    changeStatusSuccess: (
      state,
      payload: { offer: OfferFull; isChangingScope: boolean; targetScope: OfferScope },
    ) =>
      pipeState(adapter.upsertOne(state, payload.offer), next =>
        adapter.markAsLoaded(next, { scope: null, entities: [payload.offer.id] }),
      ),
    changeStatusFailed: (
      state,
      payload: { error: HttpErrorResponse & { errorFields: any }; id: number },
    ) => adapter.setError(state, payload.error, { scope: null, entities: [payload.id] }),
  }),
});

const bySlug = memoizeSelector((slug: string) =>
  createSelector(selectors.entities({ scope: null }), (state: OfferFull[]) =>
    state.find(offer => offer.slug === slug),
  ),
);

const byIdOrSlug = memoizeSelector((identifier: number | string) =>
  createSelector(selectors.entities({ scope: null }), (state: OfferFull[]) =>
    state.find(offer => offer.slug === identifier || offer.id === identifier),
  ),
);

const metaByIdOrSlug = memoizeSelector((identifier: number | string) => selectors.meta(identifier));

const actions = {
  changeStatus: defaultActions.changeStatus,
  changeStatusFailed: defaultActions.changeStatusFailed,
  changeStatusSuccess: defaultActions.changeStatusSuccess,
  create: defaultActions.create,
  createFailed: defaultActions.createFailed,
  createSuccess: defaultActions.createSuccess,
  duplicate: defaultActions.duplicate,
  duplicateFailed: defaultActions.duplicateFailed,
  duplicateSuccess: defaultActions.duplicateSuccess,
  fetchOne: defaultActions.fetchOne,
  fetchOneFailed: defaultActions.fetchOneFailed,
  fetchOneSuccess: defaultActions.fetchOneSuccess,
  follow: defaultActions.follow,
  followFailed: defaultActions.followFailed,
  followSuccess: defaultActions.followSuccess,
  unfollow: defaultActions.unfollow,
  unfollowFailed: defaultActions.unfollowFailed,
  unfollowSuccess: defaultActions.unfollowSuccess,
  update: defaultActions.update,
  updateFailed: defaultActions.updateFailed,
  updateOne: defaultActions.updateOne,
  updateSuccess: defaultActions.updateSuccess,
  removeOne: defaultActions.removeOne,
};
const TalentPoolsFullSelectors = {
  ...selectors,
  bySlug,
  byIdOrSlug,
  metaByIdOrSlug,
};

export {
  actions as TalentPoolsFullActions,
  TalentPoolsFullSelectors,
  namespace as TalentPoolsFullNamespace,
  reducer as TalentPoolsFullReducers,
};
